import { createSlice, createSelector } from "@reduxjs/toolkit";
import { ofType } from "redux-observable";
import { of } from "rxjs";
import { switchMap, map, catchError } from "rxjs/operators";
import { selectCustomerTokenEntities } from "@nxplatform/store-shared/customer/shared-customer-selectors";
import { selectRecommendedCategoriesListEnabled, selectCategoryRecommendedEnabled, selectMainRecommendedNonAuthEnabled } from "@nxplatform/platform-settings/platform-settings.selectors";
const CASINO_RECOMMENDED_GAMES_FEATURE_KEY = "casinoRecommendedGames";
var RecommendedCategoriesId = /* @__PURE__ */ ((RecommendedCategoriesId2) => {
  RecommendedCategoriesId2["main"] = "main-recommended";
  RecommendedCategoriesId2["forYouSimilar"] = "for-you-similar";
  RecommendedCategoriesId2["becauseYouLike"] = "because-you-like";
  RecommendedCategoriesId2["forYouNew"] = "for-you-new";
  RecommendedCategoriesId2["youMayAlsoLike"] = "you-may-also-like";
  RecommendedCategoriesId2["trendingGames"] = "trending-games";
  RecommendedCategoriesId2["yourForgottenFavorites"] = "your-forgotten-favorites";
  RecommendedCategoriesId2["yourFavoriteMechanic"] = "your-favorite-mechanic";
  RecommendedCategoriesId2["yourFavoriteProvidersBest"] = "your-favorite-provider-s-best";
  RecommendedCategoriesId2["topInLocation"] = "top-games-in-your-location";
  RecommendedCategoriesId2["favorites"] = "your-favorites";
  RecommendedCategoriesId2["popupSimilar"] = "popup-similar";
  return RecommendedCategoriesId2;
})(RecommendedCategoriesId || {});
var RecommendedGameType = /* @__PURE__ */ ((RecommendedGameType2) => {
  RecommendedGameType2["live"] = "live";
  RecommendedGameType2["slots"] = "slots";
  return RecommendedGameType2;
})(RecommendedGameType || {});
const initialCasinoRecommendedGamesState = {
  entities: {},
  categories: [],
  loaded: false,
  reload: false,
  fetching: false,
  error: null,
  gamesListsEmpty: true,
  trackingInfo: null,
  lang: void 0
};
const CasinoRecommendedGamesSlice = createSlice({
  name: CASINO_RECOMMENDED_GAMES_FEATURE_KEY,
  initialState: initialCasinoRecommendedGamesState,
  reducers: {
    getCasinoRecommendedGamesStart: (state, action) => {
      state.loaded = false;
      state.reload = false;
      state.fetching = true;
      state.gamesListsEmpty = true;
    },
    getCasinoRecommendedGamesSuccess: (state, action) => {
      const { data, params } = action.payload;
      const { isMobile } = params || {};
      const perPage = isMobile ? 8 : 25;
      state.entities = data.reduce((result, category) => {
        result[category.categoryId] = category;
        result[category.categoryId].title = category.categoryName;
        result[category.categoryId].total = category.games.length;
        result[category.categoryId].per_page = perPage;
        result[category.categoryId].pages = Math.ceil(category.games.length / perPage);
        if (category == null ? void 0 : category.games.length) {
          state.gamesListsEmpty = false;
        }
        return result;
      }, {});
      state.loaded = true;
      state.reload = false;
      state.fetching = false;
    },
    getCasinoRecommendedGamesFailure: (state, action) => {
      state.error = action.payload;
      state.loaded = true;
      state.fetching = false;
    },
    clearCasinoRecommendedGamesState: (state) => {
      state.loaded = false;
      state.reload = false;
      state.fetching = false;
      state.entities = initialCasinoRecommendedGamesState.entities;
    },
    setRecommendedGameStartTrackingInfo: (state, action) => {
      state.trackingInfo = action.payload;
    },
    clearRecommededGamesStartTracking: (state) => {
      state.trackingInfo = null;
    },
    setRecommendGamesLang: (state, action) => {
      state.reload = state.loaded && state.lang !== action.payload;
      state.lang = action.payload;
    }
  }
});
const CasinoRecommendedGamesReducer = CasinoRecommendedGamesSlice.reducer;
const {
  getCasinoRecommendedGamesStart,
  getCasinoRecommendedGamesSuccess,
  getCasinoRecommendedGamesFailure,
  clearCasinoRecommendedGamesState,
  setRecommendedGameStartTrackingInfo,
  clearRecommededGamesStartTracking,
  setRecommendGamesLang
} = CasinoRecommendedGamesSlice.actions;
const getCasinoRecommendedGamesState = (rootState) => rootState[CASINO_RECOMMENDED_GAMES_FEATURE_KEY];
const selectCasinoRecommendedGamesEntities = createSelector(
  getCasinoRecommendedGamesState,
  (s) => s.entities
);
const selectCasinoRecommendedGamesEntitiesArray = createSelector(
  getCasinoRecommendedGamesState,
  (s) => Object.values(s.entities)
);
const selectCasinoRecommendedGamesEntitiesByCategoryId = createSelector(
  getCasinoRecommendedGamesState,
  (s, categoryId) => categoryId,
  (s, categoryId) => s.entities[categoryId] || {}
);
const selectCasinoRecommendedGamesLoaded = createSelector(
  getCasinoRecommendedGamesState,
  (s) => s.loaded
);
const selectCasinoRecommendedGamesError = createSelector(
  getCasinoRecommendedGamesState,
  (s) => s.error
);
const selectRecommendedGamesIsEmpty = createSelector(
  getCasinoRecommendedGamesState,
  (s) => s.gamesListsEmpty
);
const selectRecommendedGameTrackingInfo = createSelector(
  getCasinoRecommendedGamesState,
  (s) => s.trackingInfo
);
const selectRecommendedGamesFromSettings = createSelector(
  getCasinoRecommendedGamesState,
  selectRecommendedCategoriesListEnabled,
  (state, recommendedCategories) => Object.values(state.entities).filter(
    (category) => recommendedCategories ? !!recommendedCategories[category.categoryId] : false
  )
);
const selectRecommendedGamesFromSettingsEmpty = createSelector(
  selectRecommendedGamesFromSettings,
  (categories) => !categories.length
);
const selectRecommendGamesLang = createSelector(
  getCasinoRecommendedGamesState,
  (s) => s == null ? void 0 : s.lang
);
const selectRecommendedGamesReload = createSelector(
  getCasinoRecommendedGamesState,
  (s) => s.reload
);
const selectRecommendedGamesFetching = createSelector(
  getCasinoRecommendedGamesState,
  (s) => s.fetching
);
const selectRecommendedGamesEnabled = createSelector(
  selectCustomerTokenEntities,
  selectCategoryRecommendedEnabled,
  selectMainRecommendedNonAuthEnabled,
  selectRecommendGamesLang,
  (auth, recommendedEnabled, recommendedNonAuthEnabled, lang) => {
    return !!(recommendedEnabled && (!!auth || recommendedNonAuthEnabled && !!lang));
  }
);
const fetchCasinoRecommendedGamesEpic = (action$, state$, { Api }) => action$.pipe(
  ofType(getCasinoRecommendedGamesStart.type),
  switchMap((action) => Api.casinoSession.getRecommendedGames(action.payload).pipe(
    map((data) => {
      return getCasinoRecommendedGamesSuccess({ data, params: action.payload });
    }),
    catchError((error) => of(getCasinoRecommendedGamesFailure(error)))
  ))
);
const fetchCasinoRecommendedGames = (params) => ({
  type: getCasinoRecommendedGamesStart.type,
  payload: params
});
export {
  CASINO_RECOMMENDED_GAMES_FEATURE_KEY,
  CasinoRecommendedGamesReducer,
  CasinoRecommendedGamesSlice,
  RecommendedCategoriesId,
  RecommendedGameType,
  clearCasinoRecommendedGamesState,
  clearRecommededGamesStartTracking,
  fetchCasinoRecommendedGames,
  fetchCasinoRecommendedGamesEpic,
  getCasinoRecommendedGamesFailure,
  getCasinoRecommendedGamesStart,
  getCasinoRecommendedGamesState,
  getCasinoRecommendedGamesSuccess,
  initialCasinoRecommendedGamesState,
  selectCasinoRecommendedGamesEntities,
  selectCasinoRecommendedGamesEntitiesArray,
  selectCasinoRecommendedGamesEntitiesByCategoryId,
  selectCasinoRecommendedGamesError,
  selectCasinoRecommendedGamesLoaded,
  selectRecommendGamesLang,
  selectRecommendedGameTrackingInfo,
  selectRecommendedGamesEnabled,
  selectRecommendedGamesFetching,
  selectRecommendedGamesFromSettings,
  selectRecommendedGamesFromSettingsEmpty,
  selectRecommendedGamesIsEmpty,
  selectRecommendedGamesReload,
  setRecommendGamesLang,
  setRecommendedGameStartTrackingInfo
};
